import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import {
  mergeReducedMotionPreset,
  resolveExpandTransition,
  useThemeMotion,
} from '../../animation/themeMotion'
import { ChromePrimaryButton } from '../ChromeActions'
import { ChromeComposerTextarea } from '../ChromeComposerTextarea'
import { chromeIconButtonClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { IconNavAgent } from '../nav-icons'
import { AgentInlinePicker, type AgentInlineOption } from './AgentInlinePicker'
import { AgentSkillSlashMenu } from './AgentSkillSlashMenu'

type AgentComposerDockProps = {
  angular: boolean
  goal: string
  mode: string
  model: string
  running: boolean
  canRun: boolean
  collapsed: boolean
  modeOptions: AgentInlineOption[]
  modelOptions: AgentInlineOption[]
  unavailableHint?: string
  onGoalChange: (value: string) => void
  onModeChange: (value: string) => void
  onModelChange: (value: string) => void
  onCollapsedChange: (collapsed: boolean) => void
  onSubmit: () => void
  headerLeading?: ReactNode
  /** 工具开关选择器（多选下拉），缺省不渲染 */
  toolsPicker?: ReactNode
  /** 可引用的技能选项（value = publicId）；为空时斜杠引用不生效 */
  skillOptions?: AgentInlineOption[]
  /** 已引用技能的选项列表（渲染引用 chips） */
  selectedSkills?: AgentInlineOption[]
  /** 切换引用某个技能（斜杠菜单选中时调用） */
  onSkillToggle?: (value: string) => void
  /** 移除某个已引用技能（chip 上的删除按钮） */
  onSkillRemove?: (value: string) => void
}

/** 斜杠引用查询：匹配输入末尾的「/查询词」（/ 前须是行首或空白），缺省返回 null */
function matchSlashQuery(goal: string): { matchText: string; query: string } | null {
  const match = goal.match(/(?:^|\s)\/([^\s/]*)$/u)
  if (!match) return null
  return { matchText: match[0], query: match[1] }
}

/** expanded=true → 朝下（收起）；expanded=false → 朝上（展开） */
function ChevronToggleIcon({ expanded: open, className }: { expanded: boolean; className?: string }) {
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {open ? <path d="M6 9l6 6 6-6" /> : <path d="M18 15l-6-6-6 6" />}
    </svg>
  )
}

export function AgentComposerDock({
  angular,
  goal,
  mode,
  model,
  running,
  canRun,
  collapsed,
  modeOptions,
  modelOptions,
  unavailableHint,
  onGoalChange,
  onModeChange,
  onModelChange,
  onCollapsedChange,
  onSubmit,
  headerLeading,
  toolsPicker,
  skillOptions = [],
  selectedSkills = [],
  onSkillToggle,
  onSkillRemove,
}: AgentComposerDockProps) {
  const expanded = !collapsed
  const motionPreset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const expandTrans = resolveExpandTransition(
    mergeReducedMotionPreset(motionPreset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )

  // ---- 斜杠技能引用 ----
  const textareaAnchorRef = useRef<HTMLDivElement>(null)
  const [highlightIndex, setHighlightIndex] = useState(0)
  const slashQuery = useMemo(() => matchSlashQuery(goal), [goal])
  // 无技能也开菜单：显示空态提示（否则用户不知道技能未配置，输入 / 毫无反馈）
  const slashMenuOpen = Boolean(slashQuery) && expanded
  const slashEmptyHint =
    skillOptions.length === 0
      ? '暂无技能'
      : '没有匹配的技能，换个关键词试试。'
  const filteredSkills = useMemo(() => {
    const query = (slashQuery?.query ?? '').trim().toLowerCase()
    if (!query) return skillOptions
    return skillOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        (option.description ?? '').toLowerCase().includes(query),
    )
  }, [skillOptions, slashQuery])
  const activeHighlight = Math.min(highlightIndex, Math.max(0, filteredSkills.length - 1))

  const closeSlashMenu = (cleanedGoal: string) => {
    setHighlightIndex(0)
    onGoalChange(cleanedGoal)
  }

  const selectSkillOption = (option: AgentInlineOption) => {
    const query = slashQuery
    onSkillToggle?.(option.value)
    if (query) {
      const cleaned = goal.slice(0, goal.length - query.matchText.length).trimEnd()
      closeSlashMenu(cleaned)
    }
  }

  const expandedBody = (
    <div className="flex flex-col gap-2 pt-2.5">
      {selectedSkills.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedSkills.map((skill) => (
            <span
              key={skill.value}
              className={[
                'inline-flex max-w-full items-center gap-1 px-2 py-1 text-[11px] font-medium',
                angular
                  ? 'rounded-none border border-line'
                  : 'rounded-full border border-[color-mix(in_srgb,var(--accent)_35%,transparent)]',
                'bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] text-ink',
              ].join(' ')}
              title={skill.description || skill.label}
            >
              {/* 内嵌移除位：平时是技能图标，悬浮变 ×，点击即移除——不占独立位置 */}
              <button
                type="button"
                aria-label={`移除技能 ${skill.label}`}
                className="group flex shrink-0 items-center"
                onClick={() => onSkillRemove?.(skill.value)}
              >
                <IconNavAgent
                  className="h-3 w-3 text-ink-soft transition group-hover:hidden"
                  aria-hidden
                />
                <svg
                  className="hidden h-3 w-3 text-ink-soft transition group-hover:block group-hover:text-ink"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
              <span className="min-w-0 truncate">{skill.label}</span>
            </span>
          ))}
        </div>
      ) : null}
      <div ref={textareaAnchorRef} className="relative">
        <ChromeComposerTextarea
          angular={angular}
          variant="composer"
          value={goal}
          onChange={(event) => onGoalChange(event.target.value)}
          onKeyDown={(event) => {
            // 斜杠技能菜单打开时：↑/↓ 导航、Enter 选中、Escape 关闭，均不触发发送
            if (slashMenuOpen && filteredSkills.length > 0) {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setHighlightIndex((activeHighlight + 1) % filteredSkills.length)
                return
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault()
                setHighlightIndex((activeHighlight - 1 + filteredSkills.length) % filteredSkills.length)
                return
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                event.preventDefault()
                selectSkillOption(filteredSkills[activeHighlight])
                return
              }
            }
            // 回车发送；Shift+Enter 换行，输入法组词中的回车不触发
            if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
            event.preventDefault()
            if (canRun && !running) onSubmit()
          }}
          placeholder={
            canRun
              ? '描述你的目标，输入 / 可引用技能；智能体会自行检索所需上下文与工具。'
              : unavailableHint || '智能体暂不可用，请联系管理人员。'
          }
          disabled={!canRun || running}
          className="max-lg:min-h-0 lg:min-h-[5.25rem]"
          textareaClassName="min-h-[3.35rem] text-[14px] sm:min-h-[4.2rem]"
        />
      </div>
      <AgentSkillSlashMenu
        open={slashMenuOpen}
        options={filteredSkills}
        selectedValues={selectedSkills.map((skill) => skill.value)}
        highlightIndex={activeHighlight}
        emptyHint={slashEmptyHint}
        onSelect={selectSkillOption}
        anchorRef={textareaAnchorRef}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <AgentInlinePicker
            value={mode}
            options={modeOptions}
            onChange={onModeChange}
            disabled={running || !canRun}
            ariaLabel="智能体模式"
            menuTitle="执行模式"
            minMenuWidth={220}
          />
          <AgentInlinePicker
            value={model}
            options={modelOptions}
            onChange={onModelChange}
            disabled={running || !canRun}
            ariaLabel="智能体模型"
            menuTitle="选择模型"
            minMenuWidth={280}
          />
          {toolsPicker}
        </div>
        <ChromePrimaryButton density="compact" onClick={onSubmit} disabled={running || !canRun}>
          {running ? '执行中…' : '发送目标'}
        </ChromePrimaryButton>
      </div>
    </div>
  )

  return (
    <div className="create-prompt-composer-dock agent-composer-dock flex min-h-0 w-full min-w-0 max-w-full shrink-0 flex-col">
      <div className="mx-auto flex w-full max-w-3xl min-w-0 flex-col px-3 py-2 sm:px-5 sm:py-2.5 lg:px-6">
        <div
          className={[
            'agent-composer-dock__header flex min-w-0 items-center justify-between gap-2',
            expanded ? (angular ? 'border-b-2 pb-2.5' : 'border-b pb-2.5') : '',
          ].join(' ')}
        >
          <div className="min-w-0">{headerLeading}</div>
          <ChromeTooltip content={expanded ? '收起' : '展开'} angular={angular} side="top" align="end">
            <button
              type="button"
              onClick={() => onCollapsedChange(!collapsed)}
              className={[chromeIconButtonClass(angular), 'shadow-none hover:shadow-none'].join(' ')}
              aria-expanded={expanded}
              aria-label={expanded ? '收起输入区域' : '展开输入区域'}
            >
              <ChevronToggleIcon expanded={expanded} className="h-4 w-4" />
            </button>
          </ChromeTooltip>
        </div>

        {reducedMotion ? (
          expanded ? expandedBody : null
        ) : (
          <AnimatePresence initial={false} mode="wait">
            {expanded ? (
              <motion.div
                key="agent-composer-expanded"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={expandTrans}
                className="overflow-visible"
              >
                {expandedBody}
              </motion.div>
            ) : null}
          </AnimatePresence>
        )}
      </div>
    </div>
  )
}
